import { CartItem } from '../types';

/**
 * Category minimums and the home delivery fee.
 *
 * Meeting any one category's minimum earns free home delivery. Ordering below
 * every minimum is still allowed, it just carries the delivery fee configured
 * by the admin (Products tab -> Order Settings).
 *
 * This lives in one place because the cart summary and the payment modal must
 * always quote the same total. Two copies of this rule drifting apart would
 * show the customer one price and charge them another.
 */

export interface MinimumCategory {
  name: string;
  min_order_quantity?: number;
}

export interface MinimumCheck {
  /** True when at least one category minimum is satisfied. */
  meets: boolean;
  /** The closest unmet minimum, for prompting the customer. */
  category?: string;
  required?: number;
  current?: number;
  /** How many more units of `category` would earn free delivery. */
  shortfall?: number;
}

/** 'Mums' and 'Hardy Mums' are the same thing and must count together. */
const normalizeCategoryName = (name: string): string => {
  const lower = (name || '').toLowerCase();
  return lower === 'mums' || lower === 'hardy mums' ? 'hardy mums' : lower;
};

export const checkCategoryMinimums = (
  items: CartItem[],
  categories: MinimumCategory[]
): MinimumCheck => {
  if (items.length === 0) return { meets: false };

  // Total units per category, with the mum variants folded together.
  const categoryTotals: { [key: string]: number } = {};
  items.forEach(item => {
    const key = normalizeCategoryName(item.category);
    categoryTotals[key] = (categoryTotals[key] || 0) + item.quantity;
  });

  let meets = false;
  let closestUnmet: MinimumCheck | null = null;

  for (const categoryName in categoryTotals) {
    const total = categoryTotals[categoryName];

    const category = categories.find(cat => {
      const dbName = normalizeCategoryName(cat.name);
      return dbName === categoryName;
    });

    // A category with no real minimum satisfies the rule on its own.
    if (!category || !category.min_order_quantity || category.min_order_quantity <= 1) {
      meets = true;
      break;
    }

    if (total >= category.min_order_quantity) {
      meets = true;
      break;
    }

    const shortfall = category.min_order_quantity - total;
    if (!closestUnmet || shortfall < (closestUnmet.shortfall ?? Infinity)) {
      closestUnmet = {
        meets: false,
        category: category.name,
        required: category.min_order_quantity,
        current: total,
        shortfall
      };
    }
  }

  if (meets) return { meets: true };
  return closestUnmet ?? { meets: false };
};

/**
 * The fee actually charged: nothing when a minimum is met, otherwise the
 * admin-configured home delivery fee.
 */
export const getDeliveryFee = (meetsMinimums: boolean, configuredFee: number): number =>
  meetsMinimums ? 0 : configuredFee;
